import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";
import CarRepairIcon from "@mui/icons-material/CarRepair";
import HomeIcon from "@mui/icons-material/Home";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  formatOrgName,
  isSyntheticEmail,
  RolesRow,
} from "pages/components/SidebarOrgUserExtras";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import toast from "react-hot-toast";
import Logo from "assets/images/logos/flex2logo.png";
import { cc } from "afms/theme/commandCenterTheme";
import {
  BACKOFFICE_ORG_ID,
  SYSTEM_DISPLAY_NAMES,
  getAppMode,
  getDefaultRoute,
  selectUserDetails,
  systemNameForAppMode,
} from "config/appMode";
import { AssetService, setMySystemDefaultRoute } from "api/users.api";
import { getUser, logoutUser } from "redux-store/auth/actions";
import { getLcpPortalRoutes } from "./LcpPortalNavigation";
import { visuallyHidden } from "@mui/utils";
import useInboxUnreadCount from "notifications/hooks/useInboxUnreadCount";
import MySettingsDialog from "./MySettingsDialog";

const LCP_SYSTEM_NAME = "LiftConnect Pro";
// Red brand accent used for the LiftConnect Pro label + matching the
// CarRepairIcon used in the DefaultSystemPicker entry.
const LCP_ACCENT = "#d32f2f";
const LCP_ACCENT_HOVER = "#ef5350";

const itemSx = (active, depth) => ({
  mx: 1.25,
  mb: 0.5,
  pl: 1.4 + depth * 1.8,
  pr: 1.25,
  py: depth === 0 ? 1.05 : 0.85,
  borderRadius: "8px",
  color: active ? "#fff" : "rgba(255,255,255,0.72)",
  bgcolor: active ? cc.blue : "transparent",
  boxShadow: active ? "0 10px 20px rgba(13,110,253,0.28)" : "none",
  "&:hover": {
    bgcolor: active ? cc.blue : "rgba(13,110,253,0.18)",
    color: "#fff",
  },
  "& .MuiListItemIcon-root": {
    color: "inherit",
    minWidth: depth === 0 ? 34 : 24,
  },
});

const routePattern = (route) => {
  if (!route) return null;
  const escaped = route.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${escaped.replace(/:[^/]+/g, "[^/]+")}(?:/)?$`);
};

const routeMatches = (route, pathname) => {
  if (!route) return false;
  if (route.includes(":")) return routePattern(route).test(pathname);
  return pathname === route || pathname.startsWith(`${route}/`);
};

const routeHasActiveChild = (route, pathname) => {
  if (routeMatches(route.route, pathname)) return true;
  return (route.collapse || []).some((child) => routeHasActiveChild(child, pathname));
};

const collectActiveKeys = (routeList, pathname, keys = {}) => {
  routeList.forEach((route) => {
    const routeKey = route.key || route.name;
    if (route.collapse && routeHasActiveChild(route, pathname)) {
      keys[routeKey] = true;
      collectActiveKeys(route.collapse, pathname, keys);
    }
  });
  return keys;
};

// Sidebar group expansion is persisted in localStorage so it survives the
// sidebar remounting on navigation (each portal page mounts its own layout).
// Keyed separately from the Environmental sidebar's storage so switching
// systems doesn't leak open-state between them.
const OPEN_KEYS_STORAGE = "lcpPortalSidebarOpenKeys";

// OrganizationSystems catalog id for LiftConnect Pro — the same id the
// Settings → Assets page filters on (lcp/pages/Settings/Assets).
const LCP_PLATFORM_SYSTEM_ID = 4;

// Last known "does this org own any LCP lifts" answer, per org, so the Lift
// Control Center group doesn't flash in or out on every reload while the
// count is re-asked. null = never asked.
const OWN_LIFTS_STORAGE = "lcpPortalSidebarHasOwnLifts";

const readStoredHasOwnLifts = (orgId) => {
  try {
    const value = window.localStorage.getItem(`${OWN_LIFTS_STORAGE}:${orgId}`);
    if (value === "1") return true;
    if (value === "0") return false;
    return null;
  } catch (e) {
    return null;
  }
};

const writeStoredHasOwnLifts = (orgId, hasOwnLifts) => {
  try {
    window.localStorage.setItem(`${OWN_LIFTS_STORAGE}:${orgId}`, hasOwnLifts ? "1" : "0");
  } catch (e) {
    /* localStorage unavailable — the group just re-settles after each reload */
  }
};

const readStoredOpenKeys = () => {
  try {
    const raw = window.localStorage.getItem(OPEN_KEYS_STORAGE);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeStoredOpenKeys = (keys) => {
  try {
    window.localStorage.setItem(OPEN_KEYS_STORAGE, JSON.stringify(keys));
  } catch (e) {
    /* localStorage unavailable — expansion just won't persist across reloads */
  }
};

/**
 * Sidebar for the LiftConnect Pro system. Parallel of EnvironmentalPortalSidebar
 * with the LCP branding (red CarRepairIcon, "LiftConnect Pro" label) and
 * the LCP nav config. Mounted by LcpPortalShell when App.js's
 * pickSidebarVariant returns SIDEBAR_VARIANTS.LCP_PORTAL.
 *
 * Reuses the per-user pinned-home machinery automatically:
 * systemNameForAppMode("lcp") → "LiftConnect Pro", so the home-pin
 * button writes to UserSystemDefaultRoutes keyed by the LCP system id.
 */
function LcpPortalSidebar({ onNavigate = () => {}, onSwitchSystem }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const user = useSelector((state) => state.auth.user);
  // Normalize the two coexisting state.auth.user shapes — see
  // config/appMode.selectUserDetails for the why.
  const userDetails = selectUserDetails(user);

  // Org-systems drives the "LiftConnect Pro" badge under the logo.
  const hasLiftConnectPro = useMemo(() => {
    const systems = userDetails?.organizationSystems;
    return Array.isArray(systems) && systems.includes(LCP_SYSTEM_NAME);
  }, [userDetails?.organizationSystems]);

  // Home for the logo click — defaults to the post-login landing path
  // for the user's current system (LCP here, since this sidebar only
  // mounts in lcp mode).
  const homeRoute = useMemo(
    () => getDefaultRoute(userDetails) || "/lcp/lift-control-center",
    [userDetails]
  );

  // Whether the caller's org owns any LCP lifts — gates the Lift Control
  // Center group. Asked on mount, then again on each navigation while the
  // answer is "none", so adding the first lift in Settings → Assets brings
  // the group back on the next click. A failed count keeps the last answer.
  const orgId = userDetails?.organizationId;
  const [hasOwnLifts, setHasOwnLifts] = useState(() => readStoredHasOwnLifts(orgId));
  const ownLiftsCheckedFor = useRef(null);

  useEffect(() => {
    if (orgId == null) return undefined;
    // Once this org is known to own lifts there's nothing left to watch for.
    if (hasOwnLifts === true && ownLiftsCheckedFor.current === orgId) return undefined;
    let cancelled = false;
    AssetService.getUserAssets({
      platformSystemId: LCP_PLATFORM_SYSTEM_ID,
      pageNumber: 1,
      pageSize: 1,
    })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data ?? {};
        const total = Number(payload?.totalRecords);
        const rows = Array.isArray(payload?.data) ? payload.data.length : 0;
        const owns = (Number.isFinite(total) ? total : rows) > 0;
        ownLiftsCheckedFor.current = orgId;
        setHasOwnLifts(owns);
        writeStoredHasOwnLifts(orgId, owns);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [orgId, pathname, hasOwnLifts]);

  // Org-context for per-item gates. "Management Core" shows for any org
  // that isn't itself managed by an enterprise (isManaged), mirroring the
  // AFMS sidebar.
  const orgContext = useMemo(
    () => ({
      isEnterpriseOrganization:
        userDetails?.isEnterpriseOrganization === true,
      isManaged: userDetails?.isManagedByEnterprise === true,
      role: userDetails?.role || "",
      secondaryRoles: Array.isArray(userDetails?.secondaryRoles)
        ? userDetails.secondaryRoles
        : [],
      hasOwnLifts,
      // Backoffice sidebar toggles; missing => shown (stale session).
      showLiftControlCenter: userDetails?.showLiftControlCenter !== false,
      showInspectionCommandCenter: userDetails?.showInspectionCommandCenter !== false,
    }),
    [
      userDetails?.isEnterpriseOrganization,
      userDetails?.isManagedByEnterprise,
      userDetails?.role,
      userDetails?.secondaryRoles,
      hasOwnLifts,
      userDetails?.showLiftControlCenter,
      userDetails?.showInspectionCommandCenter,
    ]
  );

  const visibleRoutes = useMemo(() => getLcpPortalRoutes(null, orgContext), [orgContext]);
  // The same cross-system inbox (and the same unread count) as Environmental's sidebar.
  const showsInbox = useMemo(() => {
    const has = (list) => list.some((r) => r.badge === "inbox" || has(r.collapse || []));
    return has(visibleRoutes);
  }, [visibleRoutes]);
  const inboxUnread = useInboxUnreadCount(showsInbox);

  const [openKeys, setOpenKeys] = useState(readStoredOpenKeys);

  // Auto-open the group containing the active route.
  useEffect(() => {
    setOpenKeys((current) => ({ ...current, ...collectActiveKeys(visibleRoutes, pathname) }));
  }, [visibleRoutes, pathname]);

  // Persist expansion so it survives sidebar remounts and page reloads.
  useEffect(() => {
    writeStoredOpenKeys(openKeys);
  }, [openKeys]);

  const handleRoute = (route) => {
    if (route.href) {
      window.open(route.href, "_blank", "noreferrer");
      return;
    }
    if (route.route) {
      navigate(route.route);
      onNavigate();
    }
  };

  const renderRoute = (route, depth = 0) => {
    if (route.type === "title") {
      return (
        <Typography
          key={route.key}
          variant="caption"
          sx={{
            display: "block",
            px: 2.5,
            pt: 2,
            pb: 0.75,
            color: "rgba(255,255,255,0.42)",
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
          }}
        >
          {route.title}
        </Typography>
      );
    }

    if (route.type === "divider") {
      return <Divider key={route.key} sx={{ borderColor: "rgba(255,255,255,0.08)", my: 1 }} />;
    }

    const key = route.key || route.name || route.route;
    const active = routeHasActiveChild(route, pathname);
    const hasChildren = route.collapse?.length > 0;
    // `noCollapse: true` means the parent is a plain link to `route.route`
    // and its children render as indented siblings, always visible — no
    // expand/collapse caret. `noCollapse: false` (or absent) keeps the
    // dropdown group behavior.
    const isFlatGroup = hasChildren && route.noCollapse && route.route;
    const open = Boolean(openKeys[key]) || isFlatGroup;

    return (
      <React.Fragment key={key}>
        <ListItemButton
          selected={active}
          sx={itemSx(active, depth)}
          onClick={() => {
            if (isFlatGroup || !hasChildren) {
              handleRoute(route);
            } else {
              setOpenKeys((current) => ({ ...current, [key]: !open }));
            }
          }}
        >
          {route.icon && <ListItemIcon>{route.icon}</ListItemIcon>}
          <ListItemText
            primary={route.name}
            primaryTypographyProps={{
              fontSize: depth === 0 ? 13 : 12,
              fontWeight: active ? 800 : 600,
              noWrap: true,
            }}
          />
          {route.badge === "inbox" && inboxUnread > 0 && (
            <Box
              component="span"
              data-testid="lcp-inbox-unread"
              sx={{
                ml: 1,
                flexShrink: 0,
                minWidth: 18,
                height: 18,
                px: 0.6,
                borderRadius: 9,
                bgcolor: cc.red,
                color: "#fff",
                fontSize: 10,
                fontWeight: 800,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {inboxUnread > 99 ? "99+" : inboxUnread}
              <Box component="span" sx={visuallyHidden}>
                {" "}
                unread
              </Box>
            </Box>
          )}
          {hasChildren && !isFlatGroup &&
            (open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
        </ListItemButton>
        {hasChildren && isFlatGroup && (
          <List disablePadding>
            {route.collapse.map((child) => renderRoute(child, depth + 1))}
          </List>
        )}
        {hasChildren && !isFlatGroup && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List disablePadding>
              {route.collapse.map((child) => renderRoute(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // Org name preference order matches EnvironmentalPortalSidebar: server-loaded
  // userDetails.organizationName first; fall back through legacy fields.
  const orgName =
    userDetails?.organizationName ||
    userDetails?.businessName ||
    "LiftConnect Pro";

  const userDisplayName = (() => {
    // firstName/lastName live on the outer UserProfile (user), not on
    // the inner UserDetailsDto (userDetails). Check both.
    const first = (userDetails?.firstName || user?.firstName)?.trim();
    const last = (userDetails?.lastName || user?.lastName)?.trim();
    const full = [first, last].filter(Boolean).join(" ");
    // Filter synthetic emails (server-created when no real email was
    // supplied) so the sidebar never shows "jdoe@no-email.flex2.local".
    const realEmail = (e) => (isSyntheticEmail(e) ? null : e);
    return (
      full ||
      user?.displayName ||
      userDetails?.displayName ||
      user?.username ||
      userDetails?.username ||
      realEmail(user?.email) ||
      realEmail(userDetails?.email) ||
      "—"
    );
  })();

  // "Set as my home" — pins the current pathname as the user's home for
  // the current system. Same rules as EnvironmentalPortalSidebar's pin: hidden
  // for backoffice (org 1) and when getAppMode/systemNameForAppMode
  // doesn't resolve to a known system. Icon flips green when pinned.
  const currentSystemName = userDetails
    ? systemNameForAppMode(getAppMode(userDetails))
    : null;
  const pinnedRouteForSystem = currentSystemName
    ? userDetails?.systemDefaultRoutes?.[currentSystemName] || null
    : null;
  const isHomePinned = pinnedRouteForSystem === pathname;
  const showHomePinButton =
    userDetails != null &&
    userDetails.organizationId !== BACKOFFICE_ORG_ID &&
    currentSystemName != null;
  const [pinningHome, setPinningHome] = useState(false);
  // My settings (time zone, Jam 9b489c14) — opened from the user block below:
  // click your name, or the settings icon beside Sign out.
  const [mySettingsOpen, setMySettingsOpen] = useState(false);

  const handleSetHome = async () => {
    if (pinningHome || !currentSystemName) return;
    setPinningHome(true);
    try {
      await setMySystemDefaultRoute(currentSystemName, pathname);
      await dispatch(getUser());
      const label =
        SYSTEM_DISPLAY_NAMES[getAppMode(userDetails)] || currentSystemName;
      toast.success(`Set as your ${label} home`);
    } catch (err) {
      console.error("Failed to set home route:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't save your home page. Try again.";
      toast.error(message);
    } finally {
      setPinningHome(false);
    }
  };

  // Logout — clear auth state and bounce to /login. Await so the token
  // is gone from localStorage before the navigate runs.
  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login");
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(180deg, ${cc.navy} 0%, ${cc.navy2} 100%)`,
        color: "#fff",
      }}
    >
      <Box sx={{ px: 2, pt: 2.25, pb: 1.5 }}>
        {/* Logo → user's home (LCP default unless they've pinned a route). */}
        <Box
          component={NavLink}
          to={homeRoute}
          onClick={onNavigate}
          sx={{
            display: "inline-block",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover img": { opacity: 0.85 },
          }}
          aria-label="Go to home"
        >
          <Box
            component="img"
            src={Logo}
            alt="Flex2"
            sx={{ width: 160, maxWidth: "100%", display: "block" }}
          />
        </Box>
        {/* System badge → opens the system picker so the user can switch.
            Renders as a button only when an onSwitchSystem handler is
            wired; otherwise it's a static label. Red to match LCP brand. */}
        {hasLiftConnectPro && (
          <Box
            component={onSwitchSystem ? "button" : "div"}
            type={onSwitchSystem ? "button" : undefined}
            onClick={onSwitchSystem}
            aria-label={onSwitchSystem ? "Switch to a different system" : undefined}
            sx={{
              mt: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: LCP_ACCENT,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              background: "none",
              border: "none",
              p: 0,
              cursor: onSwitchSystem ? "pointer" : "default",
              "&:hover": onSwitchSystem
                ? { color: LCP_ACCENT_HOVER, textDecoration: "underline" }
                : undefined,
            }}
          >
            <CarRepairIcon sx={{ fontSize: 14 }} />
            LiftConnect Pro
          </Box>
        )}
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <List component="nav" dense disablePadding>
          {visibleRoutes.map((route) => renderRoute(route))}
        </List>
      </Box>
      <Box
        sx={{
          mx: 1.25,
          mb: 2,
          p: 1.25,
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "10px",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.55)",
            fontSize: 9,
            display: "block",
            textTransform: "uppercase",
          }}
        >
          Organization
        </Typography>
        <Typography variant="body2" sx={{ color: "#fff", fontWeight: 800, fontSize: 12 }}>
          {formatOrgName(orgName, userDetails)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            color: "rgba(255,255,255,0.55)",
            fontSize: 9,
            display: "block",
            textTransform: "uppercase",
          }}
        >
          User
        </Typography>
        <Tooltip title="My settings — your time zone" placement="top">
          <Box
            component="button"
            type="button"
            onClick={() => setMySettingsOpen(true)}
            aria-label={`My settings for ${userDisplayName}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.25,
              p: 0,
              border: "none",
              background: "none",
              color: "#fff",
              cursor: "pointer",
              textAlign: "left",
              "&:hover .lcp-user-name": { textDecoration: "underline" },
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }} />
            <Typography
              className="lcp-user-name"
              variant="body2"
              component="span"
              sx={{ color: "#fff", fontWeight: 700, fontSize: 12 }}
            >
              {userDisplayName}
            </Typography>
          </Box>
        </Tooltip>
        <RolesRow userDetails={userDetails} />
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Chip
            icon={<CircleIcon sx={{ color: "#2ed66f !important" }} />}
            label="Operational"
            size="small"
            sx={{
              height: 22,
              bgcolor: "rgba(46,214,111,0.12)",
              color: "rgba(255,255,255,0.88)",
              fontSize: 10,
              fontWeight: 700,
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {showHomePinButton ? (
              <Tooltip
                title={
                  isHomePinned
                    ? `This is your ${currentSystemName} home`
                    : `Set this page as my home for ${currentSystemName}`
                }
                placement="top"
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleSetHome}
                    disabled={pinningHome}
                    aria-label="Set as my home"
                    sx={{
                      color: isHomePinned ? "#2ed66f" : "rgba(255,255,255,0.65)",
                      "&:hover": { color: isHomePinned ? "#4be287" : "#fff" },
                    }}
                  >
                    {isHomePinned ? (
                      <HomeIcon fontSize="small" />
                    ) : (
                      <HomeOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title="My settings" placement="top">
              <IconButton
                size="small"
                onClick={() => setMySettingsOpen(true)}
                aria-label="My settings"
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  "&:hover": { color: "#fff" },
                }}
              >
                <ManageAccountsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sign out" placement="top">
              <IconButton
                size="small"
                onClick={handleLogout}
                aria-label="Logout"
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  "&:hover": { color: "#ff8e7a" },
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <MySettingsDialog open={mySettingsOpen} onClose={() => setMySettingsOpen(false)} />
    </Box>
  );
}

LcpPortalSidebar.propTypes = {
  onNavigate: PropTypes.func,
  onSwitchSystem: PropTypes.func,
};

export default LcpPortalSidebar;
